import React from 'react';
import { useTranslation } from 'next-i18next';
import { DynamicFilter } from '../hooks/useDynamicFilters';
import { GenericFilterSection } from './GenericFilterSection';
import { PriceSection } from './PriceSection';
import { BedroomSection } from './BedroomSection';
import { AreaSection } from './AreaSection';

import { Typography, Grid } from '@mui/material';
import {
  FilterAccordion,
  FilterAccordionSummary,
  FilterAccordionDetails,
  BedroomButtonContainer,
  BedroomButton,
  FilterOptionButton,
  GenericFilterButton,
  BathroomButton,
  QuotaButton,
  StatusButton,
  FloorButton,
  ViewButton,
  DirectionButton,
  PublishedByButton,
  PostedDateButton,
  Divider
} from '../styledComponents';
import { ExpandMoreIcon } from './ExpandMoreIcon';
import {
  bathroomOptions,
  quotaOptions,
  statusOptionsLeft,
  statusOptionsRight,
  floorOptionsLeft,
  floorOptionsRight,
  viewOptionsLeft,
  viewOptionsRight,
  directionOptionsLeft,
  directionOptionsRight,
  publishedByOptions,
  postedDateOptionsLeft,
  postedDateOptionsRight
} from '../constants/filterConstants';

interface FilterOption {
  value: string;
  label: string;
}

interface DynamicFilterRendererProps {
  filter: DynamicFilter;
  isExpanded: boolean;
  onToggle: (event: React.MouseEvent) => void;
  
  priceMin?: number;
  priceMax?: number;
  minInputValue?: string;
  maxInputValue?: string;
  priceRangesMin?: number[];
  priceRangesMax?: number[];
  onMinInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMaxInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onMinPriceSelect?: (price: number) => void;
  onMaxPriceSelect?: (price: number) => void;
  
  selectedBedrooms?: string | null;
  onBedroomSelect?: (bedroom: string | null) => void;
  
  selectedBathrooms?: string | null;
  onBathroomSelect?: (bathroom: string | null) => void;
  
  areaMin?: number;
  areaMax?: number;
  areaMinInput?: string;
  areaMaxInput?: string;
  areaRangesMin?: number[];
  areaRangesMax?: number[];
  onAreaMinInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAreaMaxInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onAreaMinSelect?: (area: number) => void;
  onAreaMaxSelect?: (area: number) => void;
  
  selectedQuota?: string | null;
  onQuotaSelect?: (quota: string | null) => void;
  selectedStatus?: string | null;
  onStatusSelect?: (status: string | null) => void;
  selectedFloor?: string | null;
  onFloorSelect?: (floor: string | null) => void;
  selectedView?: string | null;
  onViewSelect?: (view: string | null) => void;
  selectedDirection?: string | null;
  onDirectionSelect?: (direction: string | null) => void;
  selectedPublishedBy?: string | null;
  onPublishedBySelect?: (publishedBy: string | null) => void;
  selectedPostedDate?: string | null;
  onPostedDateSelect?: (postedDate: string | null) => void;

  
  genericFilterSelections?: Record<number, string | null>;
  onGenericFilterSelect?: (groupId: number, value: string | null, option: any) => void;
  getFilterLayout?: (groupName: string) => 'single' | 'double';
  
  lang?: string;
  singleColumn?: boolean; // เพิ่ม prop สำหรับ layout แบบ single column
}

export const DynamicFilterRenderer: React.FC<DynamicFilterRendererProps> = ({
  filter,
  isExpanded,
  onToggle,
  ...filterProps
}) => {
  const { t } = useTranslation();
  const { componentType, group } = filter;
  
  const getAutoLayout = (optionsCount: number): 'single' | 'double' => {
    return optionsCount < 6 ? 'single' : 'double';
  };
  
  const renderFilterOptions = (
    allOptions: FilterOption[],
    selectedValue: string | null | undefined,
    onSelect: (value: string) => void,
    filterType: string
  ) => {
    // Select appropriate button component based on filter type
    const getButtonComponent = (type: string) => {
      switch (type) {
        case 'bathroom': return BathroomButton;
        case 'quota': return QuotaButton;
        case 'status': return StatusButton;
        case 'floor': return FloorButton;
        case 'view': return ViewButton;
        case 'direction': return DirectionButton;
        case 'publishedBy': return PublishedByButton;
        case 'postedDate': return PostedDateButton;
        default: return FilterOptionButton;
      }
    };
    
    const ButtonComponent = getButtonComponent(filterType);
    const title = t(`filter_labels.${filterType}`);
    const layout = getAutoLayout(allOptions.length);
    
    if (layout === 'single') {
      return (
        <>
          <FilterAccordion className={isExpanded ? 'expanded' : ''}>
            <FilterAccordionSummary 
              className={isExpanded ? 'expanded' : ''}
              onClick={onToggle}
            >
              <Typography className="section-title" variant="subtitle1" fontWeight="medium">
                {title}
              </Typography>
              <ExpandMoreIcon className="expand-icon" />
            </FilterAccordionSummary>
            {isExpanded && (
              <FilterAccordionDetails>
                <div>
                  {allOptions.map((option) => (
                    <ButtonComponent
                      key={option.value}
                      className={selectedValue === option.value ? 'selected' : ''}
                      onClick={() => onSelect(option.value)}
                      style={{ width: '100%', marginBottom: '8px' }}
                    >
                      {t(`filter_options.${filterType}.${option.value}`) || option.label}
                    </ButtonComponent>
                  ))}
                </div>
              </FilterAccordionDetails>
            )}
          </FilterAccordion>
          <Divider />
        </>
      );
    } else {
      const optionsLeft = allOptions.filter((_, index) => index % 2 === 0);
      const optionsRight = allOptions.filter((_, index) => index % 2 === 1);
      
      return (
        <>
          <FilterAccordion className={isExpanded ? 'expanded' : ''}>
            <FilterAccordionSummary 
              className={isExpanded ? 'expanded' : ''}
              onClick={onToggle}
            >
              <Typography className="section-title" variant="subtitle1" fontWeight="medium">
                {title}
              </Typography>
              <ExpandMoreIcon className="expand-icon" />
            </FilterAccordionSummary>
            {isExpanded && (
              <FilterAccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    {optionsLeft.map((option) => (
                      <ButtonComponent
                        key={option.value}
                        className={selectedValue === option.value ? 'selected' : ''}
                        onClick={() => onSelect(option.value)}
                        style={{ width: '100%', marginBottom: '8px' }}
                      >
                        {t(`filter_options.${filterType}.${option.value}`) || option.label}
                      </ButtonComponent>
                    ))}
                  </Grid>
                  <Grid item xs={6}>
                    {optionsRight.map((option) => (
                      <ButtonComponent
                        key={option.value}
                        className={selectedValue === option.value ? 'selected' : ''}
                        onClick={() => onSelect(option.value)}
                        style={{ width: '100%', marginBottom: '8px' }}
                      >
                        {t(`filter_options.${filterType}.${option.value}`) || option.label}
                      </ButtonComponent>
                    ))}
                  </Grid>
                </Grid>
              </FilterAccordionDetails>
            )}
          </FilterAccordion>
          <Divider />
        </>
      );
    }
  };
  
  const handleBathroomSelect = (bathroom: string) => {
    const newValue = filterProps.selectedBathrooms === bathroom ? null : bathroom;
    filterProps.onBathroomSelect?.(newValue);
  };

  const handleQuotaSelect = (quota: string) => {
    const newValue = filterProps.selectedQuota === quota ? null : quota;
    filterProps.onQuotaSelect?.(newValue);
  };

  const handleStatusSelect = (status: string) => {
    const newValue = filterProps.selectedStatus === status ? null : status;
    filterProps.onStatusSelect?.(newValue);
  };

  const handleFloorSelect = (floor: string) => {
    const newValue = filterProps.selectedFloor === floor ? null : floor;
    filterProps.onFloorSelect?.(newValue);
  };

  const handleViewSelect = (view: string) => {
    const newValue = filterProps.selectedView === view ? null : view;
    filterProps.onViewSelect?.(newValue);
  };

  const handleDirectionSelect = (direction: string) => {
    const newValue = filterProps.selectedDirection === direction ? null : direction;
    filterProps.onDirectionSelect?.(newValue);
  };

  const handlePublishedBySelect = (publishedBy: string) => {
    const newValue = filterProps.selectedPublishedBy === publishedBy ? null : publishedBy;
    filterProps.onPublishedBySelect?.(newValue);
  };

  const handlePostedDateSelect = (postedDate: string) => {
    const newValue = filterProps.selectedPostedDate === postedDate ? null : postedDate;
    filterProps.onPostedDateSelect?.(newValue);
  };
  
  const statusOptions = [...statusOptionsLeft, ...statusOptionsRight];
  const floorOptions = [...floorOptionsLeft, ...floorOptionsRight];
  const viewOptions = [...viewOptionsLeft, ...viewOptionsRight];
  const directionOptions = [...directionOptionsLeft, ...directionOptionsRight];
  const postedDateOptions = [...postedDateOptionsLeft, ...postedDateOptionsRight];
  
  switch (componentType) {
    case 'price':
      return (
        <PriceSection
          isExpanded={isExpanded}
          isVisible={true}
          onToggle={onToggle}
          priceMin={filterProps.priceMin || 0}
          priceMax={filterProps.priceMax || 0}
          minInputValue={filterProps.minInputValue || ''}
          maxInputValue={filterProps.maxInputValue || ''}
          priceRangesMin={filterProps.priceRangesMin || []}
          priceRangesMax={filterProps.priceRangesMax || []}
          onMinInputChange={filterProps.onMinInputChange || (() => {})}
          onMaxInputChange={filterProps.onMaxInputChange || (() => {})}
          onMinPriceSelect={filterProps.onMinPriceSelect || (() => {})}
          onMaxPriceSelect={filterProps.onMaxPriceSelect || (() => {})}
          singleColumn={filterProps.singleColumn}
        />
      );
      
    case 'bedroom':
      return (
        <BedroomSection
          isExpanded={isExpanded}
          isVisible={true}
          onToggle={onToggle}
          selectedBedrooms={filterProps.selectedBedrooms}
          onBedroomSelect={filterProps.onBedroomSelect}
        />
      );
      
    case 'bathroom':
      return renderFilterOptions(bathroomOptions, filterProps.selectedBathrooms, handleBathroomSelect, 'bathroom');
      
    case 'area':
      return (
        <AreaSection
          isExpanded={isExpanded}
          isVisible={true}
          onToggle={onToggle}
          areaMin={filterProps.areaMin || 0}
          areaMax={filterProps.areaMax || 0}
          areaMinInput={filterProps.areaMinInput || ''}
          areaMaxInput={filterProps.areaMaxInput || ''}
          areaRangesMin={filterProps.areaRangesMin || []}
          areaRangesMax={filterProps.areaRangesMax || []}
          onAreaMinInputChange={filterProps.onAreaMinInputChange || (() => {})}
          onAreaMaxInputChange={filterProps.onAreaMaxInputChange || (() => {})}
          onAreaMinSelect={filterProps.onAreaMinSelect || (() => {})}
          onAreaMaxSelect={filterProps.onAreaMaxSelect || (() => {})}
        />
      );
      
    case 'quota':
      return renderFilterOptions(quotaOptions, filterProps.selectedQuota, handleQuotaSelect, 'quota');
      
    case 'status':
      return renderFilterOptions(statusOptions, filterProps.selectedStatus, handleStatusSelect, 'status');
      
    case 'floor':
      return renderFilterOptions(floorOptions, filterProps.selectedFloor, handleFloorSelect, 'floor');
      
    case 'view':
      return renderFilterOptions(viewOptions, filterProps.selectedView, handleViewSelect, 'view');
      
    case 'direction':
      return renderFilterOptions(directionOptions, filterProps.selectedDirection, handleDirectionSelect, 'direction');
      
    case 'publishedBy':
      return renderFilterOptions(publishedByOptions, filterProps.selectedPublishedBy, handlePublishedBySelect, 'publishedBy');
      
    case 'postedDate':
      return renderFilterOptions(postedDateOptions, filterProps.selectedPostedDate, handlePostedDateSelect, 'postedDate');
      
    case 'distance':
      const selectedDistanceValue = filterProps.genericFilterSelections?.[group.group_id] || null;
      const distanceLayout = filterProps.getFilterLayout?.(group.group_name_th || group.group_name) || 'single';
      
      return (
        <GenericFilterSection
          filterGroup={group}
          isExpanded={isExpanded}
          isVisible={true}
          onToggle={onToggle}
          selectedValue={selectedDistanceValue}
          onValueSelect={(value, option) => 
            filterProps.onGenericFilterSelect?.(group.group_id, value, option)
          }
          layout={distanceLayout}
          lang={filterProps.lang}
        />
      );
      
    case 'generic':
    default:
      const selectedValue = filterProps.genericFilterSelections?.[group.group_id] || null;
      const layout = filterProps.getFilterLayout?.(group.group_name_th || group.group_name) || 'double';
      
      return (
        <GenericFilterSection
          filterGroup={group}
          isExpanded={isExpanded}
          isVisible={true}
          onToggle={onToggle}
          selectedValue={selectedValue}
          onValueSelect={(value, option) => 
            filterProps.onGenericFilterSelect?.(group.group_id, value, option)
          }
          layout={layout}
          lang={filterProps.lang}
        />
      );
  }
}; 